import type {
  Game,
  GameContext,
  SeededRng,
  Persistence,
  Economy,
  Achievements,
} from "../../engine/Game.ts";
import type { Stage } from "./render/stage.ts";
import type { Disposer } from "../../services/input/types.ts";
import { manifest } from "./manifest.ts";
import {
  makeRunState,
  isRunState,
  normalizeRunState,
} from "./domain/runState.ts";
import type { RunState } from "./domain/runState.ts";
import { compute as computeYield } from "./domain/currencyYield.ts";
import { tick as tickCreature } from "./domain/creatureBehavior.ts";
import type { PlayBounds } from "./domain/creatureBehavior.ts";
import { applyAction } from "./domain/garden.ts";
import { hitTestCreatures } from "./input/controller.ts";
import { hueFromId } from "./render/creatureSprite.ts";
import { BESTIARY } from "../../shared/franchise/bestiary.ts";
import { getTier } from "../../shared/franchise/types.ts";
import {
  createBestiaryOverlay,
  type BestiaryOverlay,
} from "./render/bestiaryOverlay.ts";
import { createShopOverlay, type ShopOverlay } from "./render/shopOverlay.ts";
import {
  createFusionOverlay,
  type FusionOverlay,
} from "./render/fusionOverlay.ts";
import { priceFor, roll } from "./domain/shop.ts";
import type { Size } from "./domain/shop.ts";
import { fuse, fusionCost } from "./domain/fusion.ts";

export { manifest };

// Play area bounds in local play-container coords.
// The play container is offset by PLAY_Y (y=8) from the canvas.
// Play area is 48×56, so local coords are x:0..47, y:0..55.
const PLAY_BOUNDS: PlayBounds = { minX: 0, maxX: 47, minY: 0, maxY: 55 };

// Native canvas dimensions
const NATIVE_W = 48;
const NATIVE_H = 80;
const PLAY_Y = 8; // play area starts at native y=8

// Minimum integer zoom used (from stage.ts)
const MIN_ZOOM = 7;

// ---------------------------------------------------------------------------
// Pixi-fallback canvas sprite representation for test environments
// ---------------------------------------------------------------------------

interface FallbackSprite {
  x: number;
  y: number;
  color: number;
}

export const createKeimgartenGame = (): Game & {
  __getRunState(): RunState;
} => {
  let runState: RunState = makeRunState();
  let persistence: Persistence | null = null;
  let economy: Economy | null = null;
  let achievements: Achievements | null = null;
  let rng: SeededRng | null = null;
  let container: HTMLElement | null = null;

  // Pixi-path: stage and sprite map
  let stage: Stage | null = null;
  const pixiSprites = new Map<number, unknown>(); // instanceId → Pixi Sprite
  let pixiAvailable = false;
  let currentZoom = MIN_ZOOM;

  // Fallback canvas path (happy-dom / no WebGL)
  let canvas: HTMLCanvasElement | null = null;
  let ctx2d: CanvasRenderingContext2D | null = null;
  const fallbackSprites = new Map<number, FallbackSprite>();

  // Input disposer
  let tapDisposer: Disposer | null = null;

  // Bestiary overlay + button
  let bestiaryOverlay: BestiaryOverlay | null = null;
  let bestiaryButton: HTMLButtonElement | null = null;

  // Shop overlay + button + balance display
  let shopOverlay: ShopOverlay | null = null;
  let shopButton: HTMLButtonElement | null = null;
  let balanceDisplay: HTMLDivElement | null = null;
  let economyDisposer: (() => void) | null = null;

  // Fusion overlay + button
  let fusionOverlay: FusionOverlay | null = null;
  let fusionButton: HTMLButtonElement | null = null;

  // ---------------------------------------------------------------------------
  // Nameplate + heart particle DOM overlay
  // ---------------------------------------------------------------------------

  const showNameplate = (
    creatureId: number,
    screenX: number,
    screenY: number,
  ): void => {
    if (container === null) return;

    const shape = BESTIARY[creatureId];
    if (shape === undefined) return;

    const tier = getTier(shape.tier);
    const text = `${shape.nameDe} — ${tier.label} (${String(shape.tier)})`;

    const div = document.createElement("div");
    div.setAttribute("data-keimgarten-nameplate", "true");
    div.textContent = text;
    div.style.cssText = [
      "position:absolute",
      "pointer-events:none",
      "background:rgba(0,0,0,0.7)",
      "color:#fff",
      "font-size:9px",
      "font-family:monospace",
      "padding:2px 4px",
      "border-radius:2px",
      "white-space:nowrap",
      `left:${String(Math.round(screenX))}px`,
      `top:${String(Math.round(screenY - 20))}px`,
      "transform:translateX(-50%)",
      "transition:opacity 0.1s",
    ].join(";");

    container.style.position = "relative";
    container.appendChild(div);

    // Fade out after ~2 s and remove
    const startMs = 1800;
    const fadeMs = 200;
    const total = startMs + fadeMs;
    const startTime = Date.now();

    const tick = (): void => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= total) {
        div.remove();
        return;
      }
      if (elapsed >= startMs) {
        const t = (elapsed - startMs) / fadeMs;
        div.style.opacity = String(1 - t);
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const showHeartParticle = (screenX: number, screenY: number): void => {
    if (container === null) return;

    const div = document.createElement("div");
    div.setAttribute("data-keimgarten-heart", "true");
    div.textContent = "♥"; // ♥
    div.style.cssText = [
      "position:absolute",
      "pointer-events:none",
      "color:#ff4466",
      "font-size:10px",
      `left:${String(Math.round(screenX))}px`,
      `top:${String(Math.round(screenY - 10))}px`,
      "transform:translateX(-50%)",
      "transition:opacity 0.3s,top 0.6s",
    ].join(";");

    container.style.position = "relative";
    container.appendChild(div);

    // Float up and fade
    const durationMs = 600;
    const startTime = Date.now();
    const startY = screenY - 10;

    const tick = (): void => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= durationMs) {
        div.remove();
        return;
      }
      const t = elapsed / durationMs;
      const curY = startY - t * 20;
      div.style.top = `${String(Math.round(curY))}px`;
      div.style.opacity = String(1 - t);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ---------------------------------------------------------------------------
  // Achievements forwarding helper
  // ---------------------------------------------------------------------------

  /**
   * Diff the before/after achievementsUnlocked arrays and forward any newly
   * unlocked IDs to the cross-game achievements service. Also updates runState.
   */
  const forwardNewAchievements = (newState: RunState): void => {
    if (achievements !== null) {
      for (const id of newState.achievementsUnlocked) {
        if (!runState.achievementsUnlocked.includes(id)) {
          achievements.unlock("002-keimgarten", id);
        }
      }
    }
    runState = newState;
  };

  // ---------------------------------------------------------------------------
  // Tap handler
  // ---------------------------------------------------------------------------

  const handleTap = (tapX: number, tapY: number): void => {
    const hit = hitTestCreatures(
      tapX,
      tapY,
      currentZoom,
      runState.owned,
      PLAY_Y,
    );
    if (hit === null) return;

    const prevCount = runState.totalTapsByCreatureId[hit.creatureId] ?? 0;

    runState = applyAction(runState, {
      type: "tap-creature",
      instanceId: hit.instanceId,
      creatureId: hit.creatureId,
      tick: runState.tick,
    });

    // Nameplate — position above the tapped creature in screen coords
    // tapX/tapY are already screen coords
    showNameplate(hit.creatureId, tapX, tapY);

    // Heart particle after 100 cumulative taps (check new count)
    const newCount = runState.totalTapsByCreatureId[hit.creatureId] ?? 0;
    if (prevCount < 100 && newCount >= 100) {
      showHeartParticle(tapX, tapY);
    } else if (newCount >= 100) {
      showHeartParticle(tapX, tapY);
    }

    // Persist
    if (persistence !== null) {
      void persistence.save("keimgarten-run", runState, { debounceMs: 50 });
    }
  };

  // ---------------------------------------------------------------------------
  // Fallback rendering (2D canvas)
  // ---------------------------------------------------------------------------

  const drawFallback = (): void => {
    if (ctx2d === null || canvas === null) return;
    ctx2d.clearRect(0, 0, canvas.width, canvas.height);
    ctx2d.fillStyle = "#ffeec0";
    ctx2d.fillRect(0, 0, canvas.width, canvas.height);

    for (const sprite of fallbackSprites.values()) {
      const r = (sprite.color >> 16) & 0xff;
      const g = (sprite.color >> 8) & 0xff;
      const b = sprite.color & 0xff;
      ctx2d.fillStyle = `rgb(${String(r)},${String(g)},${String(b)})`;
      // In fallback mode, scale native px by 4 for visibility
      const scale = 4;
      ctx2d.fillRect(
        sprite.x * scale,
        (sprite.y + PLAY_Y) * scale,
        scale,
        scale,
      );
    }
  };

  const syncFallbackSprite = (creature: {
    instanceId: number;
    creatureId: number;
    position: { x: number; y: number };
  }): void => {
    const shape = BESTIARY[creature.creatureId];
    const color = hueFromId(
      creature.creatureId,
      shape !== undefined ? shape.tier : 1,
    );
    fallbackSprites.set(creature.instanceId, {
      x: creature.position.x,
      y: creature.position.y,
      color,
    });
  };

  // ---------------------------------------------------------------------------
  // Pixi sprite management
  // ---------------------------------------------------------------------------

  const syncPixiSprite = (creature: {
    instanceId: number;
    position: { x: number; y: number };
  }): void => {
    const s = pixiSprites.get(creature.instanceId);
    if (s !== undefined) {
      const sprite = s as { position: { set(x: number, y: number): void } };
      sprite.position.set(creature.position.x, creature.position.y);
    }
  };

  // ---------------------------------------------------------------------------
  // Compute current zoom from canvas dimensions
  // ---------------------------------------------------------------------------

  const getZoom = (): number => {
    if (canvas !== null) {
      // fallback canvas is scaled by 4
      return 4;
    }
    if (stage !== null) {
      const s = stage as unknown as {
        app: { canvas: { width: number } };
      };
      return Math.max(MIN_ZOOM, Math.round(s.app.canvas.width / NATIVE_W));
    }
    return MIN_ZOOM;
  };

  // ---------------------------------------------------------------------------
  // Game contract
  // ---------------------------------------------------------------------------

  const init = async (ctx: GameContext): Promise<void> => {
    container = ctx.container;
    persistence = ctx.services.persistence;
    economy = ctx.services.economy;
    achievements = ctx.services.achievements;
    rng = ctx.rng;

    // Restore from persistence; normalizeRunState fills in fields added in
    // later schema iterations for backward-compat with older saves.
    const saved = await persistence.load<unknown>("keimgarten-run");
    if (saved !== null && isRunState(saved)) {
      runState = normalizeRunState(saved);
    } else {
      if (saved !== null) void persistence.delete("keimgarten-run");
      runState = makeRunState(ctx.rng.state);
    }

    // Subscribe to tap events
    tapDisposer = ctx.services.input.onTap((e) => {
      currentZoom = getZoom();
      // The tap event x/y are in canvas element CSS pixels.
      // In fallback mode the canvas is 4× scale. In Pixi mode the canvas
      // element is NATIVE_W*zoom × NATIVE_H*zoom. We need screen coords that
      // match what getZoom() returns, so pass them through directly.
      handleTap(e.x, e.y);
    });

    // Bestiary button (bottom HUD area)
    const btn = document.createElement("button");
    btn.dataset.role = "bestiary-button";
    btn.textContent = "Bestiary";
    btn.style.cssText = [
      "position:absolute",
      "bottom:4px",
      "right:4px",
      "min-width:80px",
      "min-height:44px",
      "background:rgba(0,0,0,0.75)",
      "color:#fff",
      "font-family:monospace",
      "font-size:10px",
      "border:1px solid #888",
      "border-radius:4px",
      "cursor:pointer",
      "z-index:10",
      "padding:4px 8px",
      "box-sizing:border-box",
    ].join(";");
    container.style.position = "relative";
    container.appendChild(btn);
    bestiaryButton = btn;

    // Bestiary overlay
    const overlay = createBestiaryOverlay();
    container.appendChild(overlay.element);
    bestiaryOverlay = overlay;

    // Initial refresh
    overlay.refresh(runState);

    // Wire button click
    btn.addEventListener("click", () => {
      overlay.show();
    });

    // ---------------------------------------------------------------------------
    // Balance display (top HUD)
    // ---------------------------------------------------------------------------
    const balanceDiv = document.createElement("div");
    balanceDiv.dataset.role = "balance-display";
    balanceDiv.style.cssText = [
      "position:absolute",
      "top:4px",
      "left:4px",
      "color:#fff",
      "font-family:monospace",
      "font-size:10px",
      "background:rgba(0,0,0,0.6)",
      "padding:2px 6px",
      "border-radius:3px",
      "pointer-events:none",
      "z-index:10",
    ].join(";");
    // economy is never null here — it was just assigned from ctx.services.economy
    const economySvc = economy;
    balanceDiv.textContent = String(economySvc.getBalance());
    container.appendChild(balanceDiv);
    balanceDisplay = balanceDiv;

    // Subscribe to economy changes to refresh balance display + shop overlay + fusion overlay
    economyDisposer = economySvc.subscribe((event) => {
      if (balanceDisplay !== null) {
        balanceDisplay.textContent = String(event.newBalance);
      }
      if (shopOverlay !== null) {
        shopOverlay.refresh(runState, event.newBalance);
      }
      if (fusionOverlay !== null) {
        fusionOverlay.refresh(runState, event.newBalance);
      }
    });

    // ---------------------------------------------------------------------------
    // Shop button
    // ---------------------------------------------------------------------------
    const shopBtn = document.createElement("button");
    shopBtn.dataset.role = "shop-button";
    shopBtn.textContent = "Shop";
    shopBtn.style.cssText = [
      "position:absolute",
      "bottom:4px",
      "left:4px",
      "min-width:60px",
      "min-height:44px",
      "background:rgba(0,0,0,0.75)",
      "color:#fff",
      "font-family:monospace",
      "font-size:10px",
      "border:1px solid #888",
      "border-radius:4px",
      "cursor:pointer",
      "z-index:10",
      "padding:4px 8px",
      "box-sizing:border-box",
    ].join(";");
    container.appendChild(shopBtn);
    shopButton = shopBtn;

    // Shop overlay
    const shopOv = createShopOverlay();
    container.appendChild(shopOv.element);
    shopOverlay = shopOv;

    // Initial refresh of shop
    shopOv.refresh(runState, economySvc.getBalance());

    // Wire Shop button click → show overlay
    shopBtn.addEventListener("click", () => {
      shopOv.show();
    });

    // ---------------------------------------------------------------------------
    // Fusion button
    // ---------------------------------------------------------------------------
    const fusionBtn = document.createElement("button");
    fusionBtn.dataset.role = "fusion-button";
    fusionBtn.textContent = "Fusion";
    fusionBtn.style.cssText = [
      "position:absolute",
      "bottom:4px",
      "left:74px",
      "min-width:60px",
      "min-height:44px",
      "background:rgba(60,20,80,0.85)",
      "color:#fff",
      "font-family:monospace",
      "font-size:10px",
      "border:1px solid #888",
      "border-radius:4px",
      "cursor:pointer",
      "z-index:10",
      "padding:4px 8px",
      "box-sizing:border-box",
    ].join(";");
    container.appendChild(fusionBtn);
    fusionButton = fusionBtn;

    // Fusion overlay
    const fusionOv = createFusionOverlay();
    container.appendChild(fusionOv.element);
    fusionOverlay = fusionOv;

    // Initial refresh
    fusionOv.refresh(runState, economySvc.getBalance());

    // Wire button click → show overlay
    fusionBtn.addEventListener("click", () => {
      fusionOv.refresh(runState, economySvc.getBalance());
      fusionOv.show();
    });

    // Wire confirm: spend (if cost > 0) → fuse → update state → persist
    fusionOv.onConfirm = (a, b, target) => {
      if (rng === null) return;

      const cost = fusionCost(target);
      if (cost > 0) {
        const spent = economySvc.spend("002-keimgarten", cost);
        if (!spent) return; // insufficient balance — overlay stays open
      }

      const result = fuse(runState, a, b, target, rng);
      if (!result.success) {
        // Defensive: refund if fuse rejected after spend
        if (cost > 0) economySvc.addYield("002-keimgarten", cost);
        return;
      }

      forwardNewAchievements(result.state);

      // Remove sprites for consumed instances
      if (pixiAvailable) {
        pixiSprites.delete(a);
        pixiSprites.delete(b);
      } else {
        fallbackSprites.delete(a);
        fallbackSprites.delete(b);
      }

      // Add sprite for new creature
      if (result.output !== null) {
        if (pixiAvailable && stage !== null) {
          const capturedStage = stage;
          const output = result.output;
          void (async () => {
            const [{ createCreatureSprite }, pixiModule] = await Promise.all([
              import("./render/creatureSprite.ts"),
              import("pixi.js"),
            ]);
            const playContainer = capturedStage.play as {
              addChild(child: unknown): void;
            };
            const sprite = createCreatureSprite(
              output,
              capturedStage.app,
              pixiModule,
            ) as { position: { set(x: number, y: number): void } };
            sprite.position.set(output.position.x, output.position.y);
            playContainer.addChild(sprite);
            pixiSprites.set(output.instanceId, sprite);
          })();
        } else {
          syncFallbackSprite(result.output);
        }
      }

      // Refresh overlays
      fusionOv.refresh(runState, economySvc.getBalance());
      bestiaryOverlay?.refresh(runState);
      shopOv.refresh(runState, economySvc.getBalance());

      // Persist
      if (persistence !== null) {
        void persistence.save("keimgarten-run", runState, { debounceMs: 50 });
      }
    };

    // Wire size tap: spend → roll → update state
    shopOv.onSizeTap = (size: Size): void => {
      if (economy === null || rng === null) return;
      const price = priceFor(size);
      const spent = economy.spend("002-keimgarten", price);
      if (!spent) return;

      // Roll creature
      const result = roll(runState, size, rng);
      forwardNewAchievements(result.state);

      // Refresh overlays
      shopOv.refresh(runState, economy.getBalance());
      bestiaryOverlay?.refresh(runState);
      fusionOverlay?.refresh(runState, economy.getBalance());

      // Add sprite for new creature
      if (pixiAvailable && stage !== null) {
        const capturedStage = stage;
        void (async () => {
          const [{ createCreatureSprite }, pixiModule] = await Promise.all([
            import("./render/creatureSprite.ts"),
            import("pixi.js"),
          ]);
          const playContainer = capturedStage.play as {
            addChild(child: unknown): void;
          };
          const sprite = createCreatureSprite(
            result.drawn,
            capturedStage.app,
            pixiModule,
          ) as { position: { set(x: number, y: number): void } };
          sprite.position.set(result.drawn.position.x, result.drawn.position.y);
          playContainer.addChild(sprite);
          pixiSprites.set(result.drawn.instanceId, sprite);
        })();
      } else {
        syncFallbackSprite(result.drawn);
      }

      // Persist
      if (persistence !== null) {
        void persistence.save("keimgarten-run", runState, { debounceMs: 50 });
      }
    };

    // Try Pixi path first
    try {
      const [{ createStage }, { createCreatureSprite }, pixiModule] =
        await Promise.all([
          import("./render/stage.ts"),
          import("./render/creatureSprite.ts"),
          import("pixi.js"),
        ]);

      stage = await createStage(ctx.container);
      stage.resize(ctx.dimensions.width, ctx.dimensions.height);

      const playContainer = stage.play as {
        addChild(child: unknown): void;
      };

      for (const creature of runState.owned) {
        const sprite = createCreatureSprite(
          creature,
          stage.app,
          pixiModule,
        ) as {
          position: { set(x: number, y: number): void };
        };
        sprite.position.set(creature.position.x, creature.position.y);
        playContainer.addChild(sprite);
        pixiSprites.set(creature.instanceId, sprite);
      }

      pixiAvailable = true;
      // Compute initial zoom from Pixi canvas
      currentZoom = getZoom();
    } catch {
      // Fallback: use a plain 2D canvas
      pixiAvailable = false;
      canvas = document.createElement("canvas");
      canvas.width = NATIVE_W * 4;
      canvas.height = NATIVE_H * 4;
      canvas.setAttribute("data-keimgarten-canvas", "true");
      container.appendChild(canvas);
      ctx2d = canvas.getContext("2d");

      for (const creature of runState.owned) {
        syncFallbackSprite(creature);
      }
      drawFallback();
      currentZoom = 4; // fallback scale
    }
  };

  const update = (_dtMs: number): void => {
    if (persistence === null || rng === null) return;
    const currentRng = rng;

    const newOwned = runState.owned.map((c) =>
      tickCreature(c, runState.tick, PLAY_BOUNDS, currentRng),
    );
    runState = { ...runState, tick: runState.tick + 1, owned: newOwned };

    // Sync sprite positions
    if (pixiAvailable) {
      for (const creature of newOwned) {
        syncPixiSprite(creature);
      }
    } else {
      for (const creature of newOwned) {
        syncFallbackSprite(creature);
      }
    }

    // Debounce-save
    void persistence.save("keimgarten-run", runState, { debounceMs: 50 });
  };

  const render = (): void => {
    if (!pixiAvailable) {
      drawFallback();
    }
    // Pixi auto-renders via its ticker; nothing to do here.
  };

  const teardown = async (): Promise<void> => {
    if (tapDisposer !== null) {
      tapDisposer();
      tapDisposer = null;
    }

    // Emit cross-game currency delta before releasing services
    if (persistence !== null && economy !== null) {
      const currentYield = computeYield(runState);
      const delta = currentYield - runState.lastYieldPaid;
      if (delta > 0) {
        economy.addYield("002-keimgarten", delta);
        runState = { ...runState, lastYieldPaid: currentYield };
        await persistence.save("keimgarten-run", runState, { debounceMs: 0 });
      }
    }

    if (pixiAvailable && stage !== null) {
      stage.destroy();
      stage = null;
    }
    pixiSprites.clear();
    pixiAvailable = false;

    if (canvas !== null && container !== null) {
      if (canvas.parentNode === container) container.removeChild(canvas);
      canvas = null;
    }
    ctx2d = null;
    fallbackSprites.clear();

    // Remove bestiary button and overlay
    if (bestiaryButton !== null) {
      bestiaryButton.remove();
      bestiaryButton = null;
    }
    if (bestiaryOverlay !== null) {
      bestiaryOverlay.destroy();
      bestiaryOverlay = null;
    }

    // Remove shop button, overlay, balance display, and subscription
    if (economyDisposer !== null) {
      economyDisposer();
      economyDisposer = null;
    }
    if (shopButton !== null) {
      shopButton.remove();
      shopButton = null;
    }
    if (shopOverlay !== null) {
      shopOverlay.destroy();
      shopOverlay = null;
    }
    if (balanceDisplay !== null) {
      balanceDisplay.remove();
      balanceDisplay = null;
    }

    // Remove fusion button and overlay
    if (fusionButton !== null) {
      fusionButton.remove();
      fusionButton = null;
    }
    if (fusionOverlay !== null) {
      fusionOverlay.destroy();
      fusionOverlay = null;
    }

    container = null;
    persistence = null;
    economy = null;
    achievements = null;
    rng = null;
  };

  return {
    init,
    update,
    render,
    teardown,
    __getRunState: () => runState,
  };
};
